/**
 * V2 Skeleton Loaders — Netflix-quality shimmer skeletons.
 * Single shimmer sweep per card, staggered wave across rows.
 */
import { cn } from '@/lib/utils';

/* ── Base shimmer block ── */
function Shimmer({ className, delay = 0 }: { className?: string; delay?: number }) {
  return (
    <div className={cn('relative overflow-hidden bg-white/[0.04] rounded-[6px]', className)}>
      <div
        className="absolute inset-0 -translate-x-full animate-[shimmer_1.8s_infinite]"
        style={{
          background: 'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.06) 50%, transparent 100%)',
          animationDelay: `${delay}ms`,
        }}
      />
    </div>
  );
}

/* ── Book card skeleton (portrait 2:3) ── */
function BookCardSkeleton({ delay = 0, ranked }: { delay?: number; ranked?: boolean }) {
  return (
    <div className={cn('shrink-0', ranked ? 'w-28 ml-4' : 'w-24')}>
      <Shimmer className="aspect-[2/3] border border-white/[0.06]" delay={delay} />
    </div>
  );
}

/* ── Book row skeleton (title + horizontal scroll) ── */
function BookRowSkeleton({ delay = 0, ranked }: { delay?: number; ranked?: boolean }) {
  return (
    <div className="space-y-2.5">
      {/* Title bar */}
      <div className="px-5">
        <Shimmer className="h-[18px] w-28 rounded-[4px]" delay={delay} />
      </div>
      {/* Scrollable card strip */}
      <div className="flex gap-2.5 px-5 overflow-hidden">
        {Array.from({ length: 6 }).map((_, i) => (
          <BookCardSkeleton key={i} delay={delay + i * 80} ranked={ranked} />
        ))}
      </div>
    </div>
  );
}

/* ── Hero skeleton (full-bleed) ── */
function HeroSkeleton() {
  return (
    <div className="relative w-full" style={{ height: '62vh', minHeight: '380px', maxHeight: '520px' }}>
      <Shimmer className="absolute inset-0 rounded-none" />
      {/* Bottom gradient to match real hero */}
      <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-background to-transparent" />
      {/* Title area */}
      <div className="absolute bottom-12 left-5 right-16 space-y-2">
        <Shimmer className="h-5 w-16 rounded-full" delay={200} />
        <Shimmer className="h-8 w-48 rounded-[4px]" delay={300} />
        <Shimmer className="h-3 w-32 rounded-[4px]" delay={400} />
      </div>
    </div>
  );
}

/* ── Square card skeleton (for jukebox tracks/audiobooks) ── */
function SquareCardSkeleton({ delay = 0 }: { delay?: number }) {
  return (
    <div className="shrink-0 w-36">
      <Shimmer className="aspect-square rounded-2xl border border-white/[0.06]" delay={delay} />
      <Shimmer className="h-3 w-24 mt-2 rounded-[3px]" delay={delay + 60} />
      <Shimmer className="h-2.5 w-16 mt-1 rounded-[3px]" delay={delay + 120} />
    </div>
  );
}

/* ── Grid card skeleton (for browse) ── */
function GridCardSkeleton({ delay = 0 }: { delay?: number }) {
  return (
    <div className="flex flex-col gap-2">
      <Shimmer className="aspect-[2/3] rounded-xl border border-white/[0.06]" delay={delay} />
      <Shimmer className="h-2.5 w-20 rounded-[3px]" delay={delay + 100} />
    </div>
  );
}

/* ═══════════════════════════════════════════
   Full-page skeleton composites
   ═══════════════════════════════════════════ */

/** V2 Home — hero + rows */
export function V2HomeSkeleton() {
  return (
    <div style={{ paddingBottom: 'calc(160px + max(env(safe-area-inset-bottom), 0px))' }}>
      <HeroSkeleton />
      <div className="space-y-6 pt-6">
        <BookRowSkeleton delay={0} ranked />
        <BookRowSkeleton delay={200} />
        <BookRowSkeleton delay={400} />
        <BookRowSkeleton delay={600} />
      </div>
    </div>
  );
}

/** V2 app gate — same surface as the first real screen, including footer chrome */
export function V2GateSkeleton() {
  return (
    <div className="dark fixed inset-0 z-50 overflow-hidden bg-background text-foreground">
      <V2HomeSkeleton />
      <div
        className="absolute bottom-0 left-0 right-0 z-50 px-4"
        style={{ paddingBottom: 'calc(max(env(safe-area-inset-bottom), 8px) + 10px)' }}
      >
        <div className="mx-auto flex w-fit max-w-full items-center gap-2.5">
          <Shimmer className="h-[62px] w-[254px] max-w-[calc(100vw-96px)] rounded-full border border-border/20" />
          <Shimmer className="h-[62px] w-[62px] shrink-0 rounded-full border border-border/20" delay={120} />
        </div>
      </div>
    </div>
  );
}

/** V2 Browse — search bar + tag pills + grid */
export function V2BrowseSkeleton() {
  return (
    <div className="px-4 pt-6 space-y-5">
      {/* Search bar */}
      <Shimmer className="h-10 w-full rounded-md" />
      {/* Tag pills */}
      <div className="flex gap-2 flex-wrap">
        {['w-[88px]', 'w-[80px]', 'w-[96px]', 'w-[72px]', 'w-[84px]'].map((w, i) => (
          <Shimmer key={i} className={`h-7 rounded-full ${w}`} delay={i * 60} />
        ))}
      </div>
      {/* Grid */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        {Array.from({ length: 6 }).map((_, i) => (
          <GridCardSkeleton key={i} delay={i * 80} />
        ))}
      </div>
    </div>
  );
}

/** V2 RoxBox/Jukebox — header + featured hero + track rows */
export function V2JukeboxSkeleton() {
  return (
    <div className="min-h-screen flex flex-col" style={{ paddingBottom: 'calc(160px + max(env(safe-area-inset-bottom), 0px))' }}>
      {/* Header */}
      <div className="px-5 pb-3" style={{ paddingTop: 'calc(env(safe-area-inset-top, 0px) + 3.5rem)' }}>
        <Shimmer className="h-7 w-44 rounded-[4px]" />
        <Shimmer className="h-3.5 w-36 mt-1.5 rounded-[3px]" delay={100} />
      </div>
      {/* Featured 2-up */}
      <div className="px-5 pb-2 grid grid-cols-2 gap-3">
        <Shimmer className="aspect-square rounded-2xl" delay={150} />
        <Shimmer className="aspect-square rounded-2xl" delay={250} />
      </div>
      {/* Track row */}
      <div className="pt-5">
        <div className="px-5 mb-3">
          <Shimmer className="h-4 w-28 rounded-[3px]" delay={300} />
        </div>
        <div className="flex gap-3 pl-5 pr-3 overflow-hidden">
          {Array.from({ length: 4 }).map((_, i) => (
            <SquareCardSkeleton key={i} delay={350 + i * 80} />
          ))}
        </div>
      </div>
      {/* Audiobook row */}
      <div className="pt-6">
        <div className="px-5 mb-3">
          <Shimmer className="h-4 w-24 rounded-[3px]" delay={600} />
        </div>
        <div className="flex gap-3 pl-5 pr-3 overflow-hidden">
          {Array.from({ length: 4 }).map((_, i) => (
            <SquareCardSkeleton key={i} delay={650 + i * 80} />
          ))}
        </div>
      </div>
    </div>
  );
}
